import MappingRulesBuilder from '@src/emp-3.0/components/MappedEventBuilder/MappingRulesBuilder';
import React, { useState, useMemo } from 'react';
import { Button, Alert, notification } from 'antd';
import { EventWrapper } from '@src/emp-3.0/components/Metrics/Flow/FlowForm/styled';
import FlowNWRReadMode from './FlowNWRReadMode';
import { isEmptyOrNotEmptyOp } from '@src/emp-3.0/utils';
import { ESystemType } from '@src/emp-3.0/types';

export const FlowNWR = ({ networkRequest, onSave, onDelete, mode }) => {
  // eslint-disable-next-line no-console

  // Check if any tag_value is empty to determine initial editing state
  const hasEmptyTagValue = useMemo(() => {
    return networkRequest.rules?.some((ruleGroup) =>
      ruleGroup.some((rule) =>
        rule.mapping_rules?.some((mappingRuleGroup) =>
          mappingRuleGroup.some((tagRuleItem) => {
            // Skip validation for empty/not empty operators as they don't require values
            if (isEmptyOrNotEmptyOp(tagRuleItem.tag_value?.op)) {
              return false;
            }
            // Check if tag_value is empty
            return !tagRuleItem.tag_value?.value?.length;
          }),
        ),
      ),
    );
  }, [networkRequest.rules]);

  const [isEditing, setIsEditing] = useState(hasEmptyTagValue);
  const [editingRules, setEditingRules] = useState(networkRequest.rules);
  const [validationError, setValidationError] = useState('');

  const handleEditIconClick = () => {
    setEditingRules(networkRequest.rules);
    setValidationError('');
    setIsEditing(true);
  };

  const handleSave = () => {
    // Validate that all tag keys and tag values are filled
    const hasEmptyFields = editingRules?.some((ruleGroup) =>
      ruleGroup.some((rule) =>
        rule.mapping_rules?.some((mappingRuleGroup) =>
          mappingRuleGroup.some((tagRuleItem) => {
            // Skip validation for empty/not empty operators as they don't require values
            if (isEmptyOrNotEmptyOp(tagRuleItem.tag_value?.op)) {
              return false;
            }

            // Check if tag_key or tag_value are empty
            const hasEmptyTagKey = !tagRuleItem.tag_key?.value?.length;
            const hasEmptyTagValue = !tagRuleItem.tag_value?.value?.length;

            return hasEmptyTagKey || hasEmptyTagValue;
          }),
        ),
      ),
    );

    // Don't save if there are empty fields
    if (hasEmptyFields) {
      setValidationError('Please fill in all required fields before saving.');
      return;
    }

    // Clean rules by removing UI-only properties
    const cleanRules = editingRules?.map((ruleGroup) =>
      ruleGroup.map((rule) => {
        // Remove empty tag rules
        const filteredMappingRules =
          rule.mapping_rules
            ?.map((mappingRuleGroup) => {
              return mappingRuleGroup.filter((tagRuleItem) => {
                return (
                  tagRuleItem.tag_key?.value?.length > 0 ||
                  tagRuleItem.tag_value?.value?.length > 0 ||
                  isEmptyOrNotEmptyOp(tagRuleItem.tag_value?.op)
                );
              });
            })
            .filter((mappingRuleGroup) => mappingRuleGroup.length > 0) ?? [];

        // Clean rule items by removing UI properties
        const cleanMappingRules = filteredMappingRules.map((mappingRuleGroup) =>
          mappingRuleGroup.map((ruleItem) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { disableTagKeySelection, ...cleanRuleItem } = ruleItem;
            return cleanRuleItem;
          }),
        );

        // Remove disable from the rule object
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { disable, disableMetaDataAddition, ...cleanRule } = rule;

        return {
          ...cleanRule,
          mapping_rules: cleanMappingRules,
        };
      }),
    );

    const updatedNetworkRequest = {
      ...networkRequest,
      isRecommended: false,
      rules: cleanRules,
    };

    onSave?.(updatedNetworkRequest);
    setValidationError(''); // Clear any validation errors
    setIsEditing(false);
  };

  const handleCancel = () => {
    setValidationError(''); // Clear any validation errors

    // tag value will be empty for the first time
    if (hasEmptyTagValue) {
      onDelete?.();
    } else {
      setIsEditing(false);
    }
  };

  const handleRulesChange = (newRules) => {
    // Clear validation error when user makes changes
    if (validationError) {
      setValidationError('');
    }

    let operatorChanged = false;

    // Process rules to automatically set operator to "match pattern" if any value contains *
    // Also check for network request tag keys and ensure proper operators
    const processedRules = newRules?.map((ruleGroup) =>
      ruleGroup.map((rule) => ({
        ...rule,
        mapping_rules: rule.mapping_rules?.map((mappingRuleGroup) =>
          mappingRuleGroup.map((ruleItem) => {
            const tagKey = ruleItem.tag_key?.value?.[0];
            const currentOp = ruleItem.tag_value?.op;

            // Check if any tag_value contains *
            const hasWildcard = ruleItem.tag_value?.value?.some(
              (val) => typeof val === 'string' && val.includes('*'),
            );

            let newOp = currentOp;

            // If wildcard is present, set to "match pattern"
            if (hasWildcard) {
              newOp = 'match pattern';
            }
            // If tag key is network_request_url_path or network_request_url_host
            // and operator is not "match pattern" or "eq", change to "eq"
            else if (
              (tagKey === 'network_request_url_path' || tagKey === 'network_request_url_host') &&
              currentOp !== 'match pattern' &&
              currentOp !== 'eq'
            ) {
              newOp = 'eq';
              operatorChanged = true;
            }

            return {
              ...ruleItem,
              tag_value: {
                ...ruleItem.tag_value,
                op: newOp,
              },
            };
          }),
        ),
      })),
    );

    // Show notification if operator was changed
    if (operatorChanged) {
      notification.info({
        message: 'Operator Changed',
        description:
          'Network request URL operators have been automatically set to "equals" for compatibility.',
        duration: 3,
        placement: 'bottomLeft',
      });
    }

    setEditingRules(processedRules);
  };

  // Process rules to add disable and disableTagKeySelection properties
  const processedEditingRules = useMemo(() => {
    if (!editingRules) return editingRules;

    const processedRules = editingRules?.map((ruleGroup) =>
      ruleGroup.map((rule) => ({
        ...rule,
        disable: true,
        disableMetaDataAddition: true,
        mapping_rules: rule.mapping_rules?.map((mappingRuleGroup) =>
          mappingRuleGroup.map((ruleItem) => ({
            ...ruleItem,
            disableTagKeySelection: ruleItem.tag_key?.value?.[0] === 'network_request_url_path',
          })),
        ),
      })),
    );

    return processedRules;
  }, [editingRules]);

  return (
    <EventWrapper>
      {isEditing ? (
        <div>
          {validationError && (
            <Alert message={validationError} type="error" showIcon style={{ marginBottom: 16 }} />
          )}
          <MappingRulesBuilder
            rules={processedEditingRules}
            onChange={handleRulesChange}
            disableAddRawEvent={true}
            src={ESystemType.EventSubject}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              alignItems: 'center',
              margin: '-12px 12px 12px 0px',
            }}
          >
            <Button onClick={handleCancel}>Cancel</Button>
            <Button style={{ color: '#0046ff', border: '1px solid #0046ff' }} onClick={handleSave}>
              Save
            </Button>
          </div>
        </div>
      ) : (
        <FlowNWRReadMode
          networkRequest={networkRequest}
          onEdit={handleEditIconClick}
          onDelete={onDelete}
          canEdit={mode !== 'view'}
        />
      )}
    </EventWrapper>
  );
};
